import { useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";

interface VoiceNotePlayerProps {
  url: string;
  duration: number;
  isOwn: boolean;
}

export function VoiceNotePlayer({ url, duration, isOwn }: VoiceNotePlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(url);
    audioRef.current = audio;

    audio.addEventListener("timeupdate", () => {
      setCurrentTime(audio.currentTime);
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    });

    audio.addEventListener("ended", () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    });

    return () => {
      audio.pause();
      audio.src = "";
    };
  }, [url]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-2 min-w-[160px]">
      <button
        onClick={togglePlay}
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isOwn ? "bg-black/20 hover:bg-black/30" : "bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30"
        }`}
      >
        {isPlaying ? (
          <Pause size={14} className={isOwn ? "text-black" : "text-[#D4AF37]"} />
        ) : (
          <Play size={14} className={isOwn ? "text-black" : "text-[#D4AF37]"} />
        )}
      </button>
      <div className="flex-1">
        <div className={`h-1 rounded-full overflow-hidden ${isOwn ? "bg-black/20" : "bg-gray-700"}`}>
          <div
            className={`h-full rounded-full transition-all ${isOwn ? "bg-black/50" : "bg-[#D4AF37]"}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className={`text-[8px] ${isOwn ? "text-black/50" : "text-gray-500"}`}>
          {isPlaying ? formatTime(currentTime) : formatTime(duration)}
        </span>
      </div>
    </div>
  );
}
